import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Event } from "@/types";
import { MapPin, Flag, User } from "lucide-react";
import ListRow from "@/components/common/ListRow";
import { formatUserName } from "@/utils/userName";

interface EventCardProps {
  event: Event;
  onView?: (event: Event) => void;
}

// Helper function to format event type for display
const formatEventType = (eventType: string): string => {
  return eventType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const EventCard: React.FC<EventCardProps> = ({ event, onView }) => {
  const { t, i18n } = useTranslation("events");
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString(i18n.language, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const isUpcoming = new Date(event.startDate) > new Date();
  const isOngoing =
    new Date(event.startDate) <= new Date() &&
    new Date(event.endDate) >= new Date();

  const handleTrackClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/tracks/${event.track.id}`);
  };

  const formatDateRange = () => {
    const start = new Date(event.startDate);
    const end = new Date(event.endDate);
    const sameDay = start.toDateString() === end.toDateString();
    const startStr = `${formatDate(event.startDate)} ${formatTime(event.startDate)}`;
    if (sameDay) {
      return `${startStr} – ${formatTime(event.endDate)}`;
    }
    return `${startStr} – ${formatDate(event.endDate)} ${formatTime(event.endDate)}`;
  };

  return (
    <ListRow>
      {/* Row 1: Title + badges */}
      <div className="flex items-center gap-2 min-w-0">
        <button
          className="text-base font-semibold text-secondary-900 cursor-pointer hover:text-primary-600 transition-colors truncate text-left"
          onClick={() => onView?.(event)}
          title={event.name}
        >
          {event.name}
        </button>
        {isUpcoming && (
          <span className="px-2 py-0.5 bg-primary-100 text-primary-800 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0">
            {t("status.upcoming")}
          </span>
        )}
        {isOngoing && (
          <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0">
            {t("status.ongoing")}
          </span>
        )}
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${
            event.visibility === "public"
              ? "bg-green-100 text-green-800"
              : event.visibility === "invitation_only"
                ? "bg-yellow-100 text-yellow-800"
                : "bg-secondary-100 text-secondary-800"
          }`}
        >
          {event.visibility === "public"
            ? t("visibility.public")
            : event.visibility === "invitation_only"
              ? t("visibility.invitationOnly")
              : t("visibility.private")}
        </span>
      </div>

      {/* Row 2: Meta — track, type, organizer, date, sessions */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs sm:text-sm text-secondary-500">
        <span className="flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
          <button
            onClick={handleTrackClick}
            className="hover:text-primary-600 hover:underline transition-colors text-left truncate"
            title={event.track.name}
          >
            {event.track.name}
          </button>
        </span>
        <span className="flex items-center gap-1">
          <Flag className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
          {formatEventType(event.eventType)}
        </span>
        <span className="flex items-center gap-1">
          <User className="h-3.5 w-3.5 text-secondary-400 flex-shrink-0" />
          {formatUserName(event.user)}
        </span>
        <span className="text-secondary-400">·</span>
        <span>{formatDateRange()}</span>
        <span className="text-secondary-400">·</span>
        <span>
          {t("fields.sessionCount", { count: event.sessions.length })}
        </span>
        {event.maxParticipants && (
          <>
            <span className="text-secondary-400">·</span>
            <span>
              {t("fields.maxParticipants")}: {event.maxParticipants}
            </span>
          </>
        )}
      </div>

      {/* Row 3 (optional): Description */}
      {event.description && (
        <p className="mt-1.5 text-sm text-secondary-600 truncate">
          {event.description}
        </p>
      )}
    </ListRow>
  );
};
